import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect
} from 'react';
import * as styles from './Tooltip.module.scss';
import useWindowResizing from '@hooks/useWindowResizing';
import useClickOutside from '@hooks/useClickOutside';
import useIsomorphicLayoutEffect from '@hooks/useIsomorphicLayoutEffect';
import classNames from 'classnames';
import { uniqueId, isNumber } from 'lodash';

const inputTags = ['button', 'input', 'select'];

export default React.memo(function Tooltip({
  id,
  className,
  contentId,
  container,
  wrapperClassName,
  children,
  text = '',
  position = 'bottom',
  isInfoPopover, // Display different UI for longer tooltips
  maxWidth,
  minLeftOffset,
  maxRightOffset,
  forceHidden,
  forceVisible,
  addAriaTag = true,
  delay,
  offset,
  onMouseEnter,
  ...restProps
}) {
  const tooltipId = useMemo(() => {
    return id ?? uniqueId('tooltip-');
  }, [id]);

  let hasAddedAriaTag = false;

  const wrapper = useRef();
  const tooltipWrapper = useRef();
  const [viewportOffset, setViewportOffset] = useState(null);
  const [containerMaxWidth, setContainerMaxWidth] = useState(null);

  const positionTooltip = useCallback(() => {
    if (!tooltipWrapper.current || !text) {
      return;
    }

    const hasMinLeftProp = isNumber(minLeftOffset);
    const hasMaxRightProp = isNumber(maxRightOffset);
    const hasMaxWidthProp = isNumber(maxWidth);

    let minLeftPos = minLeftOffset;
    let maxRightSpace = maxRightOffset;
    let maxTooltipWidth = maxWidth;

    if (!hasMinLeftProp || !hasMaxRightProp || !hasMaxWidthProp) {
      const style = getComputedStyle(tooltipWrapper.current);
      const minOffset =
        parseInt(style.getPropertyValue('--min-tooltip-offset')) || 0;

      if (!hasMinLeftProp) minLeftPos = minOffset;
      if (!hasMaxRightProp) maxRightSpace = minOffset;

      if (!hasMaxWidthProp) {
        maxTooltipWidth =
          parseInt(style.getPropertyValue('--default-tooltip-max-width')) ||
          400;
      }
    }

    // Ensure max width is not wider than container
    const containerWidth = container?.current?.offsetWidth ?? window.innerWidth;
    maxTooltipWidth = Math.min(
      maxTooltipWidth,
      containerWidth - minLeftOffset - maxRightOffset
    );

    if (tooltipWrapper.current.offsetWidth > maxTooltipWidth) {
      tooltipWrapper.current.style.setProperty(
        '--max-tooltip-width',
        `${maxTooltipWidth}px`
      );
      setContainerMaxWidth(maxTooltipWidth);
    }

    let maxRightPos = window.innerWidth - maxRightSpace;

    if (container?.current) {
      const containerRect = container.current.getBoundingClientRect();

      minLeftPos += Math.round(containerRect.left);
      maxRightPos = Math.round(containerRect.right - maxRightSpace);
    }

    const tooltipRect = tooltipWrapper.current.getBoundingClientRect();
    const { left, right } = tooltipRect;

    // First check the left offset and offset the tooltip
    // if it is outside the left bounds fo the container
    let offset = left < minLeftPos ? -left + minLeftPos : null;

    // Next, we check if the right side of the tooltip is
    // outside the right bounds of the container. Because we
    // previously ensured that the max width of the tooltip is
    // at most the same width as its container, if leftOffset is
    // null we know that setting the right offset will not
    // result in the tooltip going outside the left bounds
    if (offset === null && right > maxRightPos) {
      offset = maxRightPos - tooltipRect.right;
    }

    setViewportOffset(Math.round(offset));
  }, [text, minLeftOffset, maxRightOffset, container, maxWidth]);

  useIsomorphicLayoutEffect(positionTooltip, [positionTooltip, text]);
  useWindowResizing({ delay: 300, complete: positionTooltip });

  // Close on escape key and prevent other event
  // listeners from firing if currently hovered
  const captureEscapeKey = useRef(false);
  const [wasClosedByEsc, setWasClosedByEsc] = useState(false);

  useEffect(() => {
    const handleKeydown = e => {
      if (
        !captureEscapeKey.current ||
        (e.key !== 'Escape' && e.key !== 'Esc')
      ) {
        return;
      }

      e.stopPropagation();
      e.stopImmediatePropagation();

      setWasClosedByEsc(true);
      captureEscapeKey.current = false;
    };

    document.addEventListener('keydown', handleKeydown, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  useClickOutside(wrapper, () => {
    if (!captureEscapeKey.current) return;
    setWasClosedByEsc(false);
    captureEscapeKey.current = false;
  });

  // Hack to try to ensure tooltip is positioned correctly in modal
  const [hasPositioned, setHasPositioned] = useState(false);

  const hasContent = !!text;

  const content =
    !hasContent || !addAriaTag
      ? children
      : React.Children.map(children, child => {
          if (!hasAddedAriaTag && React.isValidElement(child)) {
            if (
              (contentId && child.props.id === contentId) ||
              (!contentId && inputTags.includes(child.type))
            ) {
              hasAddedAriaTag = true;

              return React.cloneElement(child, {
                'aria-describedby': tooltipId
              });
            }
          }

          return child;
        });

  const hasMaxWidthProp = isNumber(maxWidth);
  const hasContainerMaxWidth = isNumber(containerMaxWidth);

  let maxWidthValue = null;

  if (hasMaxWidthProp && hasContainerMaxWidth) {
    maxWidthValue = Math.min(maxWidth, containerMaxWidth);
  } else {
    maxWidthValue = containerMaxWidth ?? maxWidth;
  }

  return (
    <div
      ref={wrapper}
      className={classNames(styles.wrapper, wrapperClassName, {
        [styles.visible]: hasContent && forceVisible,
        [styles.infoPopover]: hasContent && isInfoPopover,
        'tooltip-esc-closed': wasClosedByEsc
      })}
      onMouseEnter={() => {
        captureEscapeKey.current = true;

        if (!hasPositioned) {
          positionTooltip();
          setHasPositioned(true);
        }

        setWasClosedByEsc(false);
      }}
      onMouseLeave={() => {
        captureEscapeKey.current = false;
      }}
      onMouseDown={() => {
        captureEscapeKey.current = true;
        setWasClosedByEsc(false);
      }}
      {...restProps}
    >
      {content}
      {text && (
        <div
          ref={tooltipWrapper}
          id={tooltipId}
          data-position={position}
          className={classNames(styles.tooltip, className, {
            [styles.hidden]: forceHidden || !hasPositioned || wasClosedByEsc
          })}
          style={{
            '--viewport-offset': viewportOffset ? `${viewportOffset}px` : null,
            '--max-tooltip-width': maxWidthValue ? `${maxWidthValue}px` : null,
            '--tooltip-offset': isNumber(offset) ? `${offset}px` : null,
            marginTop: offset,
            transitionDelay: isNumber(delay) ? `${delay}ms` : delay
          }}
        >
          <div className={styles.contentWrapper}>
            {isInfoPopover && <div className={styles.spacer} />}
            <div
              className={styles.content}
              onClick={e => e.stopPropagation()}
              dangerouslySetInnerHTML={{ __html: text }}
            />
            <div
              className={styles.background}
              onClick={e => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
});
